import {SingleValueInput} from '../components/formelements/single-value-input';
import {MultiValueInput} from '../components/formelements/multi-value-input';
import {Textarea} from '../components/textarea';
import {ElementHelpers} from '../helpers/element-helpers';
import {Analytics} from '../helpers/analytics';
import {Scroll} from '../helpers/scroll';

// mimicking enum behaviour for possible form types
const FormType = {
	DEFAULT: 1,	// yacht-long
	SHORT: 2,	// yacht-short
	BMC: 3		// bmc
}

/**
 * SalesForce Application Form
 * @description uses the Connexys Application form API
 * @see https://help.connexys.com/hc/en-us/articles/209210905-Javascript-Application-Form-API-Basic-Setup
 */
export class SalesForceApplicationForm {
	/**
	 * @param element
	 * @param formOptions - options for the application form
	 */
	constructor(element, formOptions) {
		if (!window.cxsForm) return;

		this.element = element;
		this.formOptions = formOptions;
		this.isApplyForm = true;
		this.hasQuestionnaire = formOptions.hasQuestionnaire === 'yes';
		this.holder = null;
		this.fields = [];
		this.formType = null;
		this.applicationId = null;

		this.setFormType(this.formOptions.formId);
		this.initForm();
	}

	get classes() {
		return {
			GROUP: 'form-group',
			INPUT: 'form-group__input',
			ERROR: 'form-group__errormsg',
			LABEL: 'form-group__label',
			FIELD_GROUP: 'form-field-group',
			FULL_WIDTH: 'form-group--full-width'
		}
	}

	/**
	 * For form options:
	 * @see https://help.connexys.com/hc/en-us/articles/209210905-Javascript-Application-Form-API-Basic-Setup#formOptions
	 */
	initForm() {
		window.cxsForm.init({
			target: '#cxsFormHolder',
			server: this.formOptions.server,
			formId: this.formOptions.formId,
			jobId: this.formOptions.jobId,
			lang: this.formOptions.lang,
			scrollMargin: 81,
			textareaRows: 3,
			onInit: this.onInit.bind(this),
			onFileUpload: this.onFileUpload.bind(this),
			onBeforeSubmit: this.onBeforeSubmit.bind(this),
			onAfterSubmit: this.onAfterSubmit.bind(this),
			onAfterValidation: this.onAfterValidation.bind(this)
		});
	}

	initQuestionnaire(questionnaireId, candidateId) {
		this.isApplyForm = false;
		window.cxsForm.init({
			target: '#cxsFormHolder',
			server: this.formOptions.server,
			jobId: this.formOptions.jobId,
			questionnaireId: questionnaireId,
			candidateId: candidateId || '',
			jobAppId: this.applicationId || '',
			scrollMargin: 81,
			reset: true,
			onInit: this.onInitQuestionnaire.bind(this, questionnaireId),
			onBeforeSubmit: this.onBeforeSubmit.bind(this),
			onAfterSubmit: this.onAfterSubmit.bind(this),
			onAfterValidation: this.onAfterValidation.bind(this)
		});
	}

	/**
	 * Sets the form type from ID to readable format
	 * @param formId
	 */
	setFormType(formId) {
		switch(formId){
			case 'a0024000000zzGnAAI': //FIN, FAC and PRD
				this.formType = FormType.DEFAULT;
				break;
			case 'a005E000009cPLSQA2': //FIN
				this.formType = FormType.SHORT;
				break;
			case 'a009E00000ECWb8QAH': //FAC
				this.formType = FormType.SHORT;
				break;
			case 'a001p00000tgHQqAAM': //FIN, FAC and PRD
				this.formType = FormType.BMC;
				break;
			default:
				this.formType = FormType.DEFAULT;
		}
	}

	/**
	 *
	 */
	onInit() {
		this.transformFieldContainers();
		this.moveFormFieldsToCorrectPosition();
		this.wrapInFieldGroups();
		this.hideFields();
		this.renameTitles();
		this.updateFieldLabels();
		this.addPlaceholderTexts();
		this.acceptPrivacyStatement();
	}

	/**
	 *
	 */
	onInitQuestionnaire(questionnaireId) {
		this.transformFieldContainers();
		this.renameQuestionnaireTitle();

		const analyticsData = {
			category: 'selection_questions',
			action: 'load',
			label: 'show questionnaire',
			questionnaireId: questionnaireId,
			applicationId: this.applicationId
		};
		Analytics.measure(analyticsData, false, 'load', '_trackEvent', false);

	}

	/**
	 *
	 * @param fieldInfo {object} - read-only info about the field that triggered validation
	 */
	onAfterValidation(fieldInfo) {
		if (fieldInfo.key === 'cxsrec__cxscandidate__c.cxsrec__first_name__c' || fieldInfo.key === 'cxsrec__cxscandidate__c.cxsrec__last_name__c') {
			return this.validateName(fieldInfo);
		}

		if (fieldInfo.key === 'cxsrec__cxscandidate__c.cxsrec__mobilephone__c') {
			return this.validatePhone(fieldInfo);
		}

		this.fields.find(field => {
			if (field.name === fieldInfo.name || field.elem.name === fieldInfo.name) {
				fieldInfo.error ? field.enableErrorState() : field.disableErrorState();
			}
		});

		return null;
	}

	validateName(fieldInfo){
		const inputValue = fieldInfo.value;
		if(inputValue.length < 2){
			return 'Vul minimaal twee karakters in.';
		}
	}

	validatePhone(fieldInfo) {
		const phoneNumber = fieldInfo.value;
		// Regex from https://stackoverflow.com/a/29767609/5449101
		// eslint-disable-next-line no-useless-escape
		const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
		const valid = regex.test(phoneNumber);
		if (!valid) {
			return 'Voer een geldig telefoonnummer in';
		}
	}

	onFileUpload(status, fieldKey) {
		if(status === 'error'){
			const field = window.cxsForm.getFieldData(fieldKey);
			// Replace 5Mb to 5MB in the error message, if your file is too large
			if(field.error && field.errorMessage.includes('5Mb')){
				const errorMessage = field.errorMessage.replace('5Mb', '5MB');
				const fieldId = field.id;
				const errorField = document.getElementById(`cxsFieldError_${fieldId}`);
				if(errorField){
					// Set a very short timeout because else the 'new' error message gets overwritten by some validation of Connexys
					setTimeout(() => {
						errorField.innerHTML = errorMessage;
					}, 10);
				}
			}
		}
	}

	transformFieldContainers() {
		this.holder = document.getElementById('cxsFormHolder');
		const fieldContainers = this.holder.querySelectorAll('.cxsFieldContainer');
		fieldContainers.forEach(fieldContainer => {
			fieldContainer.classList.add(this.classes.GROUP);

			// transform different parts of the form-group
			this.transformLabels(fieldContainer);
			this.transformInputs(fieldContainer);
			if(this.isApplyForm){
				this.transformSelectsToRadioInput(fieldContainer);
				this.transformCheckBoxes(fieldContainer);
				this.transformDateFields(fieldContainer);
				this.transformTextareas(fieldContainer);
				this.addDescriptionToUploadfield(fieldContainer);
				this.addClassesToFormGroups(fieldContainer);
				this.checkOptionalFields(fieldContainer);
				this.updateHtmlOfUploadField(fieldContainer);
			} else {
				this.transformRadioButtons(fieldContainer);
			}
		});

		this.transformButtons();

		// @TODO: maybe this:
		// new FormHandler(document.getElementById('cxsFormHolder'));

	}

	/**
	 * Some fields are in the wrong order, this method moves them to the correct position
	 */
	moveFormFieldsToCorrectPosition() {
		const baseClassTpl = (name) => `.cxsFieldName_cxsrec__${name}__c`;

		this.moveElementTo(baseClassTpl('Motivation'), `.cxsDocumentsSection ${baseClassTpl('Last_CV')}`, 'after'); // move motivation to after CV

		if(this.formType === FormType.BMC){
			this.moveElementTo(baseClassTpl('Current_position'), baseClassTpl('Motivation'), 'after'); // move current position to after motivation
		}
		else{
			this.moveElementTo(baseClassTpl('Current_position'), '.cxsFieldName_MotivationLetter__c', 'after'); // move current position to after motivation
		}


		this.moveElementTo('.cxsFieldName_OptInAll__c', '#cxsSectionHeader_3', 'after'); // move OptInAll to after 'Privacy statement'-title (which is hidden). Can't use baseClassTpl() because className is different, because it's added later by Salesforce
		this.moveElementTo('.cxsFieldName_Are_you_self_employed__c', baseClassTpl('Current_position'), 'after'); // move zzp to after current position

		if(this.formType === FormType.SHORT){
			this.moveElementTo(baseClassTpl('Date_of_birth'), baseClassTpl('Last_name'), 'after'); // move date of birth to after last name
			this.moveElementTo(baseClassTpl('Linked_in_URL'),'.cxsFieldName_MotivationLetter__c', 'after'); // move linked in url to after motivation
		}
		else{
			this.moveElementTo(baseClassTpl('Gender'), baseClassTpl('Date_of_birth'), 'after'); // move gender to after DOB
			this.moveElementTo(baseClassTpl('Linked_in_URL'), baseClassTpl('Current_position'), 'after'); // move linked in url to after current position
		}
	}

	/**
	 * Hides SF fields
	 */
	hideFields() {
		// Array of selectors to hide
		['.cxsFieldName_cxsrec__Current_department__c'].forEach(selector => {
			document.querySelectorAll(selector).forEach(element => element.hidden = true);
		});
	}

	transformLabels(fieldContainer) {
		const label = fieldContainer.querySelector('label');
		if (label) label.classList.add(this.classes.LABEL);
	}

	transformInputs(fieldContainer) {
		this.wrapElementsInsideHtml(fieldContainer, 'input', 'cxstextField');
		const input = fieldContainer.querySelector('input');
		if (fieldContainer.classList.contains('cxsFieldName_cxsrec__E_mail_address__c')) {
			if (input) input.maxLength = 80;
		} else if (fieldContainer.classList.contains('cxsFieldName_cxsrec__mobilePhone__c')) {
			if (input) input.maxLength = 40;
		} else if (input) {
			input.maxLength = 255;
		}
	}

	transformTextareas(fieldContainer) {
		this.wrapElementsInsideHtml(fieldContainer, 'textarea', 'cxstextareaField');
	}

	transformRadioButtons(fieldContainer) {

		fieldContainer.classList.add(this.classes.FULL_WIDTH);
		const radioWrapper = fieldContainer.querySelector('.cxsRadios');

		const options = [];

		const radioFields = radioWrapper.childNodes;
			for (let radioField of radioFields) {
				const input = radioField.querySelector('input');
				const label = radioField.querySelector('.cxsRadioLabel');
				const option = {
					value: input.value,
					innerText: label.innerText
				};
				options.push(option)
			}

		const radioButtonElements = this.createRadioButtonsElements(options, fieldContainer.id || fieldContainer.getAttribute('cxs'));
		for (let i = 0; i < radioButtonElements.length; i++) {
			fieldContainer.appendChild(radioButtonElements[i]);
		}
		radioWrapper.hidden = true;

		// move the feedback element to the last child
		fieldContainer.appendChild(fieldContainer.querySelector('.form-group__errormsg'));
	}

	insertIcon(inputWrapper, extraClass, iconId) {
		const iconTempl = (iconId) => `
			<span class="icon">
				<svg>
					<use xlink:href="#${iconId}"></use>
				</svg>
			</span>`;
		let iconElement = document.createElement('span');
		iconElement.classList.add('form-group__icon');
		iconElement.classList.add('form-group__icon--feedback');
		iconElement.classList.add(extraClass);
		iconElement.innerHTML = iconTempl(iconId);
		inputWrapper.appendChild(iconElement);
	}

	/**
	 * Wraps required HTML around all inputs fields given by parameter
	 * @param fieldContainer
	 * @param inputType
	 * @param cxsFieldClass
	 */
	wrapElementsInsideHtml(fieldContainer, inputType, cxsFieldClass) {
		const input = fieldContainer.querySelector(inputType);
		const errorMsg = fieldContainer.querySelector('.cxsFieldError');

		if (errorMsg) errorMsg.classList.add(this.classes.ERROR);

		if (input) {
			// text fields
			if (input.classList.contains(cxsFieldClass)) {

				// create wrapper for input & feedback element
				const inputWrapperEl = document.createElement('div');
				inputWrapperEl.classList.add(this.classes.INPUT);
				inputWrapperEl.appendChild(input);

				if (inputType === 'input') {
					this.insertIcon(inputWrapperEl, 'icon--valid', 'check');
					this.insertIcon(inputWrapperEl, 'icon--error', 'warning');
				}

				// append the error message, if available
				if (errorMsg) inputWrapperEl.appendChild(errorMsg);

				// append the new structure to the field container
				fieldContainer.appendChild(inputWrapperEl);

				// Add behaviour using the SingleValueInput component
				/** @type {SingleValueInput} */
				const inputComponent = new SingleValueInput(input, this.holder);
				this.fields.push(inputComponent);
				if (inputType === 'textarea') {
					new Textarea(input);
				}
			}
		}
	}

	/**
	 * Moves one element to a different location in the DOM
	 *
	 * @param srcSelector {string} - selector for the element to move
	 * @param targetSelector {string} - selector of the target element, used to set the new position in the DOM
	 * @param position {string} - where to move the src in relation to the target (before or after)
	 */
	moveElementTo(srcSelector, targetSelector, position) {
		const srcElement = document.querySelector(srcSelector);
		const targetElement = document.querySelector(targetSelector);
		if (srcElement && targetElement) {
			switch (position) {
				case 'before':
					srcElement.parentNode.insertBefore(srcElement, targetElement);
					break;
				case 'after':
					ElementHelpers.insertElementAfter(srcElement, targetElement);
					break;
			}
		}
	}

	/**
	 * Transforms select inputs to radio inputs
	 *
	 * @param fieldContainer {HTMLElement}
	 */
	transformSelectsToRadioInput(fieldContainer) {

		fieldContainer.querySelectorAll('.cxsSelectField').forEach(field => {
			const options = field.options;
			const optionsWithValue = [];

			// only get the options with a value
			for (let option of options) {
				if (option.value.length !== 0) optionsWithValue.push(option);
			}

			const radioButtonElements = this.createRadioButtonsElements(optionsWithValue, field.id || field.getAttribute('cxs'));

			for (let i = 0; i < radioButtonElements.length; i++) {
				fieldContainer.appendChild(radioButtonElements[i]);
			}
			field.hidden = true;
		});

		fieldContainer.querySelectorAll('input[type=radio]').forEach(radioButtonEl => {
			const radioInputComponent = new MultiValueInput(radioButtonEl, this.holder);
			this.fields.push(radioInputComponent);
			radioButtonEl.addEventListener('click', () => {
				//get the key of the field, so the SF form can be updated
				let fieldName;
				// @TODO: find a way to save the fieldName, so we don't to perform these computations on every input change
				for (let className of fieldContainer.classList.values()) {
					if (className.startsWith('cxsFieldName')) {
						fieldName = className.split('cxsFieldName_')[1].toLowerCase();
						break;
					}
				}
				if (fieldName) this.setFieldValue(fieldName, radioInputComponent.elem.value);
			})
		});

		// move the feedback element to the last child
		fieldContainer.appendChild(fieldContainer.querySelector('.form-group__errormsg'));
	}

	/**
	 *
	 * @param options {Array<HTMLOptionElement>}
	 * @param name {string}
	 * @returns {Array<HTMLElement>}
	 */
	createRadioButtonsElements(options, name) {

		const singleRadioButtonTpl = (option) => `
                        <label for="radio-${option.value}">
							<span class="radio-button__input">
								<input type="radio" name="${name}" value="${option.value}" id="radio-${option.value}">
								<span class="radio-button__style" aria-hidden="true"></span>
							</span><span class="radio-button__label">${option.innerText}</span>
						</label>`;

		return options.map(option => {
			const div = document.createElement('div');
			div.classList.add(this.classes.INPUT);
			div.classList.add('radio-button');
			div.innerHTML = singleRadioButtonTpl(option);

			return div;
		});
	}

	transformCheckBoxes(fieldContainer) {

		fieldContainer.querySelectorAll('input[type=checkbox]').forEach(checkboxInput => {
			const label = fieldContainer.querySelector('label');

			const checkboxTpl = `<label>
									<span class="checkbox__input">
										<span class="icon checkbox__style" aria-hidden="true"><span class="icon">
										<svg><use xlink:href="#check"></use></svg></span></span>
									</span>
									<span class="checkbox__label">${label.innerHTML}</span>
								</label>`;

			const checkBoxEl = document.createElement('div');
			checkBoxEl.classList.add(this.classes.INPUT);
			checkBoxEl.classList.add('checkbox');
			checkBoxEl.innerHTML = checkboxTpl;

			const checkboxInputNode = checkBoxEl.querySelector('.checkbox__input');
			checkboxInputNode.insertBefore(checkboxInput, checkboxInputNode.childNodes[0]);

			fieldContainer.appendChild(checkBoxEl);
			label.hidden = true;

			const errorMsgEl = fieldContainer.querySelector('.form-group__errormsg');
			fieldContainer.appendChild(errorMsgEl);

			fieldContainer.querySelectorAll('input[type=checkbox]').forEach(checkBoxInput => {
				const checkboxComponent = new MultiValueInput(checkBoxInput, this.holder);
				this.fields.push(checkboxComponent);
			});
		});
	}

	transformDateFields(fieldContainer) {

		if (fieldContainer.classList.contains('cxsFieldType_date')) {
			const inputFields = fieldContainer.querySelectorAll('input');
			inputFields.forEach(inputField => inputField.hidden = true);

			// create new date input
			const inputWrapper = document.createElement('div');
			inputWrapper.classList.add(this.classes.INPUT);

			const dateInput = document.createElement('input');
			dateInput.type = 'text';
			dateInput.name = 'cxsrec__Date_of_birth__c';

			inputWrapper.appendChild(dateInput);
			fieldContainer.appendChild(inputWrapper);

			const errorMsgEl = fieldContainer.querySelector('.form-group__errormsg');
			fieldContainer.appendChild(errorMsgEl);

			const dateInputComponent = new SingleValueInput(dateInput, this.holder);
			this.fields.push(dateInputComponent);

			dateInput.addEventListener('blur', () => {
				const dateObj = this.parseDateString(dateInputComponent.elem.value);
				this.updateDateFields(dateObj.day, dateObj.month, dateObj.year);
			});
		}
	}

	updateDateFields(day, month, year) {
		this.setFieldValue('date_of_birth', `${year}-${month}-${day}`);
	}

	/**
	 *
	 * @param dateStr {string} - in format dd-mm-yyyy
	 * @returns {{month: (*|string), year: (*|string), day: (*|string)}}
	 */
	parseDateString(dateStr) {
		const splitDate = dateStr.split('-');

		return {
			day: splitDate[0],
			month: splitDate[1],
			year: splitDate[2]
		};
	}

	transformButtons() {
		this.holder.querySelectorAll('input[type="button"]').forEach(buttonEl => {
			if (buttonEl.classList.contains('cxsApplyButton')) {

				const buttonClasses = ['reset-button', 'link__button', 'link__button--primary', 'link__button--full-width-mobile'];

				for (let i = 0; i < buttonClasses.length; i++) {
					buttonEl.classList.add(buttonClasses[i]);
				}

				buttonEl.value = this.hasQuestionnaire && this.isApplyForm ? 'Volgende stap' : 'Sollicitatie verzenden';

				const formGroupEl = document.createElement('div');
				formGroupEl.classList.add(this.classes.GROUP);
				formGroupEl.classList.add('form-group__submit');

				formGroupEl.appendChild(buttonEl);
				this.holder.appendChild(formGroupEl);
			}
		});
	}

	/**
	 * sets the value of a field (by key) in the SF Form model/object
	 * @param key {string}
	 * @param value {string}
	 */
	setFieldValue(key, value) {
		// Look up the full key of the field in the fieldKeyList
		const allFields = window.cxsForm.getFieldKeyList();
		const fieldToUpdate = allFields.find(fieldKey => fieldKey.includes(key));

		// Update the SF field
		if (fieldToUpdate) window.cxsForm.setFieldValue(fieldToUpdate, value)
	}

	/**
	 * Gets the value from a SF field
	 * @param key {string} - key of the form field
	 * @return {*} - null if key is not found
	 */
	getFieldValue(key) {
		// Look up the full key of the field in the fieldKeyList
		const allFields = window.cxsForm.getFieldKeyList();
		const field = allFields.find(fieldKey => fieldKey.includes(key));
		return field || null;
	}

	onBeforeSubmit() {
		this.loader = document.createElement('div');
		this.loader.innerHTML = this.loaderHtml();

		this.element.appendChild(this.loader);
	}

	/**
	 * Fires after submit
	 * @param isSuccess
	 * @param results
	 * @returns {boolean} -- set to false to prevent standard connexys behaviour.
	 */
	onAfterSubmit(isSuccess, results) {
		if (isSuccess) {
			if(results.applicationId) this.applicationId = results.applicationId;
			this.holder.innerHTML = '';
			Scroll.scrollPageTo(this.holder, 500, 100);
			this.onBeforeSubmit();
			const questionnaire = results.questionnaireId;
			this.handleSubmitAnalytics(results, questionnaire);
			if (questionnaire && this.isApplyForm && this.hasQuestionnaire) {
				this.initQuestionnaire(questionnaire, results.candidateId);
			} else {
				if (this.formOptions.redirect) window.location.href = this.formOptions.redirect;
			}
		} else {
			if (this.loader) ElementHelpers.removeElement(this.loader);
		}
		return false;
	}

	handleSubmitAnalytics(results, questionnaire) {
		let analyticsData = {};
		if(questionnaire && !this.isApplyForm) {
			analyticsData = {
				category: 'selection_questions',
				action: 'click - interact',
				label: 'submit - questionnaire',
				questionnaireId: results.questionnaireId,
				applicationId: this.applicationId
			};
			Analytics.measure(analyticsData, false, 'click', '_trackEvent', false);
		} else {
			analyticsData = {
				stepName: 'application-submit',
				candidateId: results.candidateId,
				applicationId: this.applicationId,
				sollicitatieFlow: this.formOptions.formId
			};
			Analytics.measure(analyticsData, false, 'view', '_formStep', false);
		}
	}

	renameTitles() {
		const titleContainers = this.holder.querySelectorAll('.cxsSectionHeader');
		const titleArray = this.formOptions.titles;

		// Start with i=1, because i=0 is title 'solliciteren', which is hidden
		for (let i = 1; i < titleContainers.length; i++) {
			const container = titleContainers[i];
			//titleArray starts with i=0, thats why we do i-1
			container.innerHTML = titleArray[i - 1];
		}
	}

	renameQuestionnaireTitle() {
		const introduction = this.holder.querySelector('.cxsIntroduction');
		if(introduction) introduction.innerHTML = '';
		const questionnaireSection = this.holder.querySelector('.cxsQuestionnaireSection');
		const titles = this.formOptions.titles;
		const h3 = document.createElement('h3');
		h3.innerText = titles[3];
		if(questionnaireSection) questionnaireSection.parentNode.insertBefore(h3, questionnaireSection);
	}

	addDescriptionToUploadfield(fieldContainer) {
		const uploadField = fieldContainer.classList.contains('cxsFieldType_file');

		if (uploadField) {
			const label = fieldContainer.querySelector('.form-group__label');
			if (label) label.innerHTML = fieldContainer.classList.contains('cxsFieldName_MotivationLetter__c') ? 'Voeg je motivatiebrief toe' : 'Voeg je CV toe';
			let description = document.createElement('div');
			description.classList.add('form-group__feedback');
			description.innerHTML = 'doc, docx, pdf, ppt, pptx. Max 5 MB.';
			fieldContainer.appendChild(description);
			ElementHelpers.insertElementAfter(description, fieldContainer.querySelector('.cxsFileWait'));
		}
	}

	wrapInFieldGroups() {
		const fieldGroups = [
			this.holder.querySelectorAll('.cxsFieldName_cxsrec__Last_name_prefix__c, .cxsFieldName_cxsrec__Last_name__c'),
			this.holder.querySelectorAll('.cxsFieldName_cxsrec__Postcode__c, .cxsFieldName_cxsrec__Town_city__c')
		];

		fieldGroups.forEach((fieldGroup) => {
			this.wrapInFieldGroup(fieldGroup)
		})
	}

	wrapInFieldGroup(fieldGroup) {
		const fieldGroupElement = document.createElement('div');
		fieldGroupElement.classList.add(this.classes.FIELD_GROUP);

		fieldGroup[0].parentNode.insertBefore(fieldGroupElement, fieldGroup[0]);

		for (let i = 0; i < fieldGroup.length; i++) {
			fieldGroupElement.appendChild(fieldGroup[i]);
		}
	}

	addClassesToFormGroups(fieldContainer) {
		if (fieldContainer.classList.contains('cxsFieldName_cxsrec__Last_name_prefix__c') || fieldContainer.classList.contains('cxsFieldName_cxsrec__Postcode__c')) {
			fieldContainer.classList.add('form-group--col-2');
		}
		if (fieldContainer.classList.contains('cxsFieldType_textarea')) {
			fieldContainer.classList.add('form-group--col-6');
		}
	}

	updateFieldLabels() {
		const addressLabel = this.getLabelField('.cxsFieldName_cxsrec__Address_line_1__c', '.form-group__label');
		const OptInLabel = this.getLabelField('.cxsFieldName_OptInAll__c', '.checkbox__label');

		this.updateLabel(addressLabel, 'Straat + Huisnummer');
		this.updateLabel(OptInLabel, this.formOptions.optInText);
	}

	updateLabel(labelField, newText){
		if(labelField) labelField.innerHTML = newText;
	}

	getLabelField(formFieldSelector, labelFieldSelector) {
		const formField = this.holder.querySelector(formFieldSelector);
		return formField ? formField.querySelector(labelFieldSelector) : '';
	}

	checkOptionalFields(fieldContainer) {
		if (!fieldContainer.classList.contains('cxsRequired') && !fieldContainer.classList.contains('cxsFieldName_cxsrec__Last_name_prefix__c')) {
			const label = fieldContainer.querySelector('.cxsFieldLabel');
			const labelSuffix = '&nbsp;<span class="form-group__label--suffix">(Optioneel)</span>';
			label.innerHTML += labelSuffix;
		}
	}

	loaderHtml() {
		return `
			<div class="loader">
				<div class="bounce1"></div>
				<div class="bounce2"></div>
				<div class="bounce3"></div>
			</div>`;
	}

	updateHtmlOfUploadField(fieldContainer) {
		const loaderContainer = fieldContainer.querySelector('.cxsFileWait');
		if (loaderContainer) loaderContainer.innerHTML = this.loaderHtml();

		const crossIcon = fieldContainer.querySelector('.cxsFileRemove');
		const crossIconHtml = `
			<span class="icon ">
				<svg focusable="false">
				  <use xlink:href="#trash"></use>
				</svg>
			</span>
		`;
		if (crossIcon) crossIcon.innerHTML = crossIconHtml;
	}

	/**
	 * Accept privacy statement, because it shouldn't be optional. (It's hidden with CSS).
	 */
	acceptPrivacyStatement() {
		this.setFieldValue('form.privacystatement', true);
	}

	addPlaceholderTexts() {
		const birthDayInput = this.holder.querySelector('.cxsFieldName_cxsrec__Date_of_birth__c .form-group__input input');
		birthDayInput.placeholder = 'dd-mm-jjjj';
	}

	static getSelector() {
		// @TODO: improve selector for more general usage?
		return '#cxsFormHolder';
	}
}
